import { useQuery } from "@tanstack/react-query";
import { Card, Spinner, Callout, Badge, Gauge } from "@/design/components";
import { getUsage } from "@/api/admin";
import { getHealth, type OrgAccountStatus } from "@/api/health";
import { getUsageHistory, type UsageHistoryPoint } from "@/api/usage-history";
import { getTraffic, type TrafficPoint } from "@/api/traffic";
import { ApiError } from "@/api/client";
import { TIER_LABEL } from "@/features/onboarding/tiers";
import { DunningBanner } from "@/features/billing/DunningBanner";
import { SLACK_INSTALL_URL } from "@/config";
import { Link } from "react-router-dom";
import styles from "./DashboardScreen.module.css";

/**
 * Dashboard overview — tier + usage-vs-quota gauges + the connect-a-channel
 * guide. Renders inside the AppShell (the shell owns the nav/topbar chrome).
 */
export function DashboardScreen() {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["usage"],
    queryFn: ({ signal }) => getUsage(signal),
  });
  // System status (Phase 4) — account state + relay link health. Supplementary to
  // the usage gauges; its failure never blocks the dashboard.
  const healthQuery = useQuery({ queryKey: ["health"], queryFn: ({ signal }) => getHealth(signal) });
  // Usage-over-time (Phase 4) — daily snapshots; sparse until snapshots accumulate.
  const historyQuery = useQuery({ queryKey: ["usage-history"], queryFn: ({ signal }) => getUsageHistory(undefined, signal) });
  // Message volume (Phase 6) — relay-reported daily traffic; empty until the relay reports it.
  const trafficQuery = useQuery({ queryKey: ["traffic"], queryFn: ({ signal }) => getTraffic(undefined, signal) });

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="u-eyebrow">Control plane</p>
          <h1 className={styles.h1}>Overview</h1>
        </div>
        {data && <Badge tone="accent">{TIER_LABEL[data.tier] ?? data.tier} plan</Badge>}
      </header>

      {/* Dunning alert (past_due/unpaid) — renders nothing otherwise. */}
      {data && <DunningBanner status={data.subscription.status} />}

      {isPending && (
        <div className={styles.loading}>
          <Spinner size={22} color="var(--accent)" /> <span>Loading usage…</span>
        </div>
      )}

      {isError && (
        <Callout tone="danger" title="Couldn't load usage">
          {error instanceof ApiError ? error.message : "Please try again shortly."}
        </Callout>
      )}

      {data && (
        <div className={styles.grid}>
          <Card className={styles.metric}>
            <Gauge label="Collaborating agents" used={data.agents.used} limit={data.agents.limit} unit="agents" />
          </Card>
          <Card className={styles.metric}>
            <Gauge label="Channels" used={data.channels.used} limit={data.channels.limit} unit="channels" />
          </Card>
        </div>
      )}

      {/* Usage over time — daily channel snapshots (Phase 4 monitoring). */}
      {historyQuery.data && historyQuery.data.points.length > 0 && (
        <Card className={styles.historyCard}>
          <p className="u-eyebrow">Usage over time</p>
          <UsageHistoryChart points={historyQuery.data.points} rangeDays={historyQuery.data.rangeDays} />
        </Card>
      )}

      {/* Message volume — relay-reported daily traffic (Phase 6). Empty until relay-fed. */}
      {trafficQuery.data && trafficQuery.data.points.length > 0 && (
        <Card className={styles.historyCard}>
          <p className="u-eyebrow">Message volume</p>
          <TrafficChart points={trafficQuery.data.points} rangeDays={trafficQuery.data.rangeDays} />
        </Card>
      )}

      {/* System status — account state + control-plane→relay link health. */}
      <Card className={styles.statusCard}>
        <p className="u-eyebrow">System status</p>
        {healthQuery.isError ? (
          <p className={styles.statusNote}>Status is unavailable right now.</p>
        ) : (
          <dl className={styles.statusRows}>
            <div className={styles.statusRow}>
              <dt className={styles.statusLabel}>Account</dt>
              <dd className={styles.statusValue}>
                {healthQuery.data ? <AccountBadge status={healthQuery.data.org.status} /> : <Spinner size={14} color="var(--accent)" />}
              </dd>
            </div>
            <div className={styles.statusRow}>
              <dt className={styles.statusLabel}>Relay link</dt>
              <dd className={styles.statusValue}>
                {healthQuery.data ? <RelayBadge reachable={healthQuery.data.relay.reachable} /> : <Spinner size={14} color="var(--accent)" />}
              </dd>
            </div>
          </dl>
        )}
      </Card>

      <Card className={styles.guide}>
        <p className="u-eyebrow">Connect a Slack channel</p>
        <h2 className={styles.guideTitle}>Bridge a channel to your agent</h2>
        <ol className={styles.steps}>
          <li>
            <span className={styles.num}>1</span>
            <span>
              <strong>Install the Cogent Slack app</strong> in the workspace you want to connect — this
              adds the <code>/cogent</code> command and the bot. Without it, Slack replies
              "<code>/cogent</code> is not a valid command".
              <a className={styles.installCta} href={SLACK_INSTALL_URL} target="_blank" rel="noreferrer">
                Add to Slack
              </a>
            </span>
          </li>
          <li>
            <span className={styles.num}>2</span>
            <span>
              <strong>Create a channel</strong> — the portal gives you a{" "}
              <strong>channel name</strong> and a <strong>secret</strong>. Your{" "}
              <strong>Org_ID</strong> is the value revealed once when you registered — <strong>not</strong>{" "}
              the channel's "Relay session ID". Lost it? Rotate it under Settings.
              <Link className={styles.installCta} to="/app/channels">
                Create a channel
              </Link>
            </span>
          </li>
          <li>
            <span className={styles.num}>3</span>
            <span>
              <strong>Connect your agent</strong> — in your Claude Code or Codex session run{" "}
              <code>/cogent:register channel name "&lt;name&gt;", channel password "&lt;secret&gt;", peer name "&lt;your-agent&gt;", ORGID "&lt;org-id&gt;"</code>.
              The Org_ID auto-routes the agent to the Team relay (cogent-bridge 3.5.1+) — no endpoint
              setup needed.
            </span>
          </li>
          <li>
            <span className={styles.num}>4</span>
            <span>
              <strong>Public channel? Skip this</strong> — <code>/cogent map</code> (next step) adds
              the Cogent bot for you automatically. <strong>Private channel only:</strong> add the
              bot first — type <code>@Cogent</code> in the channel and click <strong>Add to channel</strong>{" "}
              in Slackbot's reply. (Typing <code>/invite @Cogent Team</code> as text does not work.)
            </span>
          </li>
          <li>
            <span className={styles.num}>5</span>
            <span>
              Run <code>/cogent map &lt;channel-name&gt; &lt;secret&gt; &lt;org-id&gt;</code> in that channel. Messages now
              relay both ways.
            </span>
          </li>
        </ol>
      </Card>
    </div>
  );
}

// Bucket dates are UTC "YYYY-MM-DD"; render in UTC so the label matches the bucket.
function shortDay(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? isoDate
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
}

/**
 * Channel-count-over-time bars. Channels are authoritative (always known); the
 * agent count is shown in each bar's label only when it was known that day
 * (relay-sourced → may be "unknown"), so the chart never implies a fabricated
 * agent figure. The caller renders this only when there is ≥1 point.
 */
function UsageHistoryChart({ points, rangeDays }: { points: UsageHistoryPoint[]; rangeDays: number }) {
  const max = Math.max(1, ...points.map((p) => p.channels));
  return (
    <figure className={styles.chart}>
      <figcaption className={styles.chartCaption}>Channels per day — last {rangeDays} days</figcaption>
      <div className={styles.bars} role="list">
        {points.map((p) => {
          const agentsLabel = p.agents === null ? "agents unknown" : `${p.agents} agent${p.agents === 1 ? "" : "s"}`;
          const label = `${p.date}: ${p.channels} channel${p.channels === 1 ? "" : "s"}, ${agentsLabel}`;
          return (
            <div key={p.date} className={styles.barCol} role="listitem" title={label} aria-label={label}>
              <div className={styles.barTrack}>
                <div className={styles.bar} style={{ height: `${Math.round((p.channels / max) * 100)}%` }} />
              </div>
              <span className={styles.barDay}>{shortDay(p.date)}</span>
            </div>
          );
        })}
      </div>
    </figure>
  );
}

/** Messages-per-day bars (relay-reported). Active agents shown in each bar's label. */
function TrafficChart({ points, rangeDays }: { points: TrafficPoint[]; rangeDays: number }) {
  const max = Math.max(1, ...points.map((p) => p.messages));
  return (
    <figure className={styles.chart}>
      <figcaption className={styles.chartCaption}>Messages per day — last {rangeDays} days</figcaption>
      <div className={styles.bars} role="list">
        {points.map((p) => {
          const label = `${p.date}: ${p.messages} message${p.messages === 1 ? "" : "s"}, ${p.activeAgents} active agent${p.activeAgents === 1 ? "" : "s"}`;
          return (
            <div key={p.date} className={styles.barCol} role="listitem" title={label} aria-label={label}>
              <div className={styles.barTrack}>
                <div className={styles.bar} style={{ height: `${Math.round((p.messages / max) * 100)}%` }} />
              </div>
              <span className={styles.barDay}>{shortDay(p.date)}</span>
            </div>
          );
        })}
      </div>
    </figure>
  );
}

const ACCOUNT_STATUS: Record<OrgAccountStatus, { tone: "success" | "warning" | "danger"; label: string }> = {
  active: { tone: "success", label: "Active" },
  suspended: { tone: "warning", label: "Suspended" },
  deleted: { tone: "danger", label: "Deleted" },
};

function AccountBadge({ status }: { status: OrgAccountStatus }) {
  const s = ACCOUNT_STATUS[status];
  return <Badge tone={s.tone}>{s.label}</Badge>;
}

/** Relay reachability is 3-state: true=Connected, false=Unreachable, null=Not monitored. */
function RelayBadge({ reachable }: { reachable: boolean | null }) {
  if (reachable === null) return <Badge tone="neutral">Not monitored</Badge>;
  return reachable ? <Badge tone="success">Connected</Badge> : <Badge tone="danger">Unreachable</Badge>;
}
